import { ref, computed, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import { debounce } from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'
import { useUtils as useI18nUtils } from '@core/libs/i18n'

export default function useList() {
  const refListTable = ref(null)
  const toast = useToast()
  const { t } = useI18nUtils()

  const tableColumns = [
    {
      label: 'ai_analytics_servers.message',
      key: 'message',
      sortable: false
    },
    {
      label: 'ai_analytics_servers.camera',
      key: 'camera.name',
      sortable: false
    },
    {
      label: 'ai_analytics_servers.server',
      key: 'server.name',
      sortable: false
    },
    {
      label: 'ai_analytics_servers.created_at',
      key: 'created_at',
      sortable: true
    }
  ]

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('created_at')
  const isSortDirDesc = ref(true)

  // Opciones para los filtros
  const messageOptions = computed(() => [
    { value: 'STREAM_ONLINE', label: t('ai_analytics_servers.STREAM_ONLINE') },
    { value: 'STREAM_CONNECTION_LOST', label: t('ai_analytics_servers.STREAM_CONNECTION_LOST') },
    { value: 'STREAM_HARD_DOWN', label: t('ai_analytics_servers.STREAM_HARD_DOWN') },
    { value: 'STREAM_LOW_QUALITY', label: t('ai_analytics_servers.STREAM_LOW_QUALITY') },
    { value: 'AUTH_ERROR', label: t('ai_analytics_servers.AUTH_ERROR') }
  ])

  // Filtros
  const idServerFilter = ref(null)
  const messageFilter = ref(null)
  const startDateFilter = ref(null)
  const endDateFilter = ref(null)
  store.dispatch('ai_analytics_servers/servers/getAll')
  const serverOptions = computed(() => store.getters['ai_analytics_servers/servers/getServersOptions'])

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const fetchList = (ctx, callback) => {
    store.dispatch('ai_analytics_servers/logs/getAll', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      id_server: idServerFilter.value,
      message: messageFilter.value,
      from: startDateFilter.value,
      to: endDateFilter.value
    })
      .then(response => {
        callback(response.data)
        totalList.value = response.meta.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const refetchData = () => {
    if (refListTable.value && refListTable.value.refresh) {
      refListTable.value.refresh()
    }
  }

  watch([currentPage, perPage], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  const filters = ref({})
  const filtersCount = useFilterCounter(filters)

  const buildFilters = () => {
    filters.value = {
      id_server: idServerFilter.value,
      message: messageFilter.value,
      start_date: startDateFilter.value,
      end_date: endDateFilter.value
    }

    refetchData()
  }

  const clearSearchFilters = () => {
    idServerFilter.value = null
    messageFilter.value = null
    startDateFilter.value = null
    endDateFilter.value = null

    buildFilters()
  }

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    serverOptions,

    messageOptions,
    idServerFilter,
    messageFilter,
    startDateFilter,
    endDateFilter,

    refetchData,
    clearSearchFilters,
    buildFilters,
    filtersCount
  }
}
